import { getCart, renderBNPLMemberships } from '@/scripts/cart/cart-bnpl-items';
import type { CartItem, DiscountApplication } from '@/scripts/shopify';

const DISCOUNT_STORAGE_KEY = 'pv:discount_code';
let cartDrawerRefreshInFlight = false;
let cartDrawerRefreshQueued = false;

function getStoredDiscountCode() {
  try {
    const value = window.localStorage.getItem(DISCOUNT_STORAGE_KEY);
    return value ? value.trim() : '';
  } catch (_error) {
    return '';
  }
}

function setStoredDiscountCode(code: string) {
  try {
    const value = (code || '').trim();
    if (!value) {
      window.localStorage.removeItem(DISCOUNT_STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(DISCOUNT_STORAGE_KEY, value);
  } catch (_error) {
    // noop
  }
}

function hasActiveDiscountChip() {
  return Boolean(document.querySelector('.cart-drawer__discount-item__name'));
}

function hydrateDiscountInputFromStorage() {
  if (hasActiveDiscountChip()) return;
  const input = document.querySelector<HTMLInputElement>('#cart-drawer-discount');
  if (!input || input.value.trim()) return;

  const storedCode = getStoredDiscountCode();
  if (!storedCode) return;
  input.value = storedCode;
}

async function refreshCartDrawerSafely() {
  if (cartDrawerRefreshInFlight) {
    cartDrawerRefreshQueued = true;
    return;
  }

  cartDrawerRefreshInFlight = true;
  try {
    await updateCartDrawer();
  } catch (error) {
    console.error('Error refreshing cart drawer:', error);
  } finally {
    cartDrawerRefreshInFlight = false;
    if (cartDrawerRefreshQueued) {
      cartDrawerRefreshQueued = false;
      void refreshCartDrawerSafely();
    }
  }
}

export function initCartFooter() {
  hydrateDiscountInputFromStorage();
  document.documentElement.addEventListener('cart:changed', function () {
    void refreshCartDrawerSafely();
  });

  document.body.addEventListener('submit', async function (event: SubmitEvent) {
    const form = event.target as HTMLFormElement;
    if (form?.id === 'CartDrawer-Discount') {
      event.preventDefault();

      const errorElement = document.querySelector<HTMLElement>('#discount-error');
      const discountInput = document.querySelector<HTMLInputElement>('#cart-drawer-discount');

      if (errorElement) errorElement.style.display = 'none';

      if (!discountInput) return;
      discountInput.classList.remove('form__input--success', 'form__input--error');
      discountInput.addEventListener('blur', () => {
        window.scrollTo(0, window.scrollY);
      });

      const discountCode = discountInput.value.trim();
      if (!discountCode) return;

      const submitButton = form.querySelector<HTMLButtonElement>('button[type="submit"]');
      const originalButtonText = submitButton ? submitButton.textContent : '';
      if (submitButton) {
        submitButton.disabled = true;
        submitButton.textContent = 'Applying...';
      }

      try {
        await fetch(window.Shopify?.routes?.root + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount: discountCode })
        });

        await updateCartDrawer();
        const cartResponse = await fetch(`${window.routes.cart_url}.js`);
        const cart = await cartResponse.json();

        const codeUpper = discountCode.toUpperCase();
        const isApplied = (cart.cart_level_discount_applications || []).some((d: DiscountApplication) =>
          d.type === 'discount_code' && d.title.toUpperCase() === codeUpper)
          || (cart.items || []).some((item: CartItem) =>
            (item.line_level_discount_allocations || []).some(d =>
              d.discount_application && d.discount_application.type === 'discount_code' && d.discount_application.title.toUpperCase() === codeUpper));

        if (isApplied) {
          setStoredDiscountCode(discountCode);
          if (window.cartAttribution?.syncDiscountCartAttributeFromCart) {
            await window.cartAttribution.syncDiscountCartAttributeFromCart(cart);
          }
          const currentInput = document.getElementById('cart-drawer-discount');
          if (currentInput) {
            currentInput.classList.add('form__input--success');
            setTimeout(() => {
              if (currentInput) {
                currentInput.classList.remove('form__input--success');
              }
            }, 3000);
          }
        } else {
          const currentInput = document.getElementById('cart-drawer-discount');
          const currentError = document.getElementById('discount-error');

          if (currentInput) {
            currentInput.classList.add('form__input--error');
          }
          if (currentError) {
            currentError.textContent = 'Code invalid. Please try a different one.';
            currentError.style.display = 'block';
          }
        }

        document.documentElement.dispatchEvent(new CustomEvent('cart:changed'));
      } catch (error) {
        console.error('Error applying discount:', error);
        const currentInput = document.getElementById('cart-drawer-discount');
        const currentError = document.getElementById('discount-error');

        if (currentInput) {
          currentInput.classList.add('form__input--error');
        }
        if (currentError) {
          currentError.textContent = 'Code invalid. Please try a different one.';
          currentError.style.display = 'block';
        }

        if (submitButton) {
          submitButton.disabled = false;
          submitButton.textContent = originalButtonText;
        }
      }
    }
  });

  document.body.addEventListener('click', async function (event: Event) {
    const discountButton = (event.target as HTMLElement)
      ?.closest<HTMLButtonElement>('.cart-drawer__discount-item__button');
    if (discountButton) {
      event.preventDefault();

      const discountCode = discountButton.getAttribute('data-discount') || '';

      const originalHTML = discountButton.innerHTML;
      discountButton.disabled = true;
      discountButton.style.opacity = '0.5';
      discountButton.style.cursor = 'not-allowed';

      try {
        const allDiscountButtons = document.querySelectorAll('.cart-drawer__discount-item__button[data-discount]');
        const remainingCodes = [];
        for (const btn of allDiscountButtons) {
          const code = btn.getAttribute('data-discount');
          if (code && code !== discountCode) {
            remainingCodes.push(code);
          }
        }

        await fetch(window.Shopify?.routes?.root + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount: '' })
        });

        for (const code of remainingCodes) {
          await fetch(window.Shopify?.routes?.root + 'discount/' + encodeURIComponent(code), {
            method: 'GET',
            redirect: 'manual'
          });
        }

        const cartResponse = await fetch(`${window.routes.cart_url}.js`);
        const updatedCart = await cartResponse.json();

        const activeCodes = (updatedCart.cart_level_discount_applications || [])
          .filter((app: DiscountApplication) => app.type === 'discount_code' && app.title)
          .map((app: DiscountApplication) => app.title.trim())
          .filter(Boolean);

        if (activeCodes.length) {
          setStoredDiscountCode(activeCodes[0]);
        } else {
          setStoredDiscountCode('');
        }

        if (window.cartAttribution?.syncDiscountCartAttributeFromCart) {
          await window.cartAttribution.syncDiscountCartAttributeFromCart(updatedCart);
        }

        if (typeof window.SegmentUser !== 'undefined' && discountCode) {
          window.SegmentUser.sendSegmentEvent(null, {
            eventName: 'Discount Removed',
            discount_code: discountCode,
            source_detail: 'sidecart',
            sent_from: 'Pvolve Web Script',
          });
        }

        await updateCartDrawer();

        document.documentElement.dispatchEvent(new CustomEvent('cart:changed'));
      } catch (error) {
        console.error('Error removing discount:', error);

        discountButton.disabled = false;
        discountButton.style.opacity = '1';
        discountButton.style.cursor = 'pointer';
        discountButton.innerHTML = originalHTML;
      }
    }
  });
}

async function updateCartDrawer() {
  const cartResponse = await fetch(window.routes.cart_url);
  const cartHTML = await cartResponse.text();

  const parser = new DOMParser();
  const doc = parser.parseFromString(cartHTML, 'text/html');

  const newItems = doc.querySelector('cart-drawer-items');
  if (newItems) {
    const cart = getCart(doc.documentElement);
    if (typeof cart !== 'undefined') {
      renderBNPLMemberships(
        cart.items.filter(
          (item) => item.properties._pay_later
        ),
        doc.documentElement,
      );
    }
    const currentItems = document.querySelector('cart-drawer-items');
    if (currentItems) {
      currentItems.innerHTML = newItems.innerHTML;
    }
  }

  const newFooter = doc.querySelector('.cart-drawer__footer');
  if (newFooter) {
    const currentFooter = document.querySelector('.cart-drawer__footer');
    if (currentFooter) {
      currentFooter.innerHTML = newFooter.innerHTML;
      hydrateDiscountInputFromStorage();
    }
  }
}
